import { useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useController, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useDebounce } from 'shared/hooks/use-debounce';
import { useCustomerSearch } from 'shared/queries';
import { Brands, HTMLUListElementAttributes } from 'shared/types';
import { isPrePopulateTermsBrand } from '../shared/utility';

export function CustomerAutocomplete() {
  const name = 'customerSearch';
  const {
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const {
    field: { value, onChange },
  } = useController({
    name,
    rules: { required: { message: 'Customer is required', value: true } },
  });

  const [isFirstOnInputChangeCall, setIsFirstOnInputChangeCall] = useState<boolean>(true);
  const { brand } = useParams<{ brand: Brands }>();
  const search: string = watch(name);
  const debouncedSearch = useDebounce(search, 300);
  const { data, isLoading } = useCustomerSearch(debouncedSearch, brand);
  const assignedSalesRep = watch('assignedSalesRep');

  return (
    <Autocomplete
      id={name}
      inputValue={value}
      onChange={async (_, value, reason) => {
        if (reason === 'clear') {
          setValue('customer.customerType', '');
        }
        if (typeof value === 'string') {
        } else if (value && value.inputValue) {
          // This is a new customer
          setValue('customer.isNew', true);
          setValue('customer.emptyType', true);
          setValue('customer.name', value?.inputValue || '');
          setValue('customer.customerType', value?.customertype || '');
          setValue('customer.customerNumber', value?.customernumber || '');
          setValue('customer.customerNumberAdjusted', value?.customernumberadjusted || '');
          setValue('customer.corpNameCustomerName', value?.corpnamecustomername || '');
          setValue('customer.companyId', value?.companyid || '');
        } else {
          setValue('customer.isExcluded', value?.customerexclusion === 'YES');
          setValue('customer.isNew', false);
          setValue('customer.name', value?.customername || '');
          setValue('customer.customerNumber', value?.customernumber || '');
          setValue('customer.customerNumberAdjusted', value?.customernumberadjusted || '');
          setValue('customer.corpNameCustomerName', value?.corpnamecustomername || '');
          setValue('customer.companyId', value?.companyid || '');
          if (isPrePopulateTermsBrand(brand)) {
            setValue('customer.paymentTerms', value?.paymenttermsdescription || 'NET 30');
            setValue('customer.assignedSalesReps', value?.salespersonname || '');
            setValue(
              'assignedSalesRep',
              value?.salespersonname ? { value: value?.salespersonname, key: value?.salespersonname } : assignedSalesRep
            );
            setValue('customer.assignedSalesRepsEmail', value?.salespersonemail || '');
          }
          if (value?.customertype) {
            setValue('customer.customerType', value?.customertype);
            setValue('customer.emptyType', false);
          } else {
            setValue('customer.customerType', '');
            setValue('customer.emptyType', true);
          }
        }
      }}
      onInputChange={(_, newValue, reason) => {
        if (reason === 'clear') {
          setValue('customer.customerType', '');
          return;
        }
        // onInputChange gets called on the first render and overides the default field value
        if (isFirstOnInputChangeCall && newValue === '') {
          setIsFirstOnInputChangeCall(false);
          return;
        } else if (isFirstOnInputChangeCall) {
          setIsFirstOnInputChangeCall(false);
        }
        onChange(newValue);
      }}
      filterOptions={(options, params) => {
        if (params.inputValue !== '') {
          const inputValue = params.inputValue;
          options.push({
            customername: '',
            corpnamecustomername: '',
            customertype: '',
            customernumber: '',
            customernumberadjusted: '',
            customerexclusion: '',
            customernumbername: '',
            companyid: '',
            uniqueid: 'new',
            addDisplayLabel: `Add "${inputValue}"`,
            paymenttermsdescription: 'NET 30',
            salespersonname: '',
            salespersonemail: '',
            inputValue,
            brand,
          });
        }
        return options;
      }}
      freeSolo
      selectOnFocus
      clearOnBlur
      isOptionEqualToValue={(option, value) => option.customername === value.customername}
      getOptionLabel={option => option.inputValue || `${option.customernumber} - ${option.corpnamecustomername}`}
      renderOption={(props, option) => (
        <li {...props}>{option.addDisplayLabel || `${option.customernumber} - ${option.corpnamecustomername}`}</li>
      )}
      options={data || []}
      fullWidth
      data-testid={`${name}-combobox`}
      ListboxProps={{ 'data-testid': `${name}-listbox` } as HTMLUListElementAttributes}
      renderInput={params => (
        <TextField
          {...params}
          name={name}
          label='Customer Name'
          variant='outlined'
          error={Boolean(errors.customerSearch)}
          helperText={errors.customerSearch?.message}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading && <CircularProgress color='inherit' size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
